/**
 * Dedup Filter Dialog Component
 *
 * Shows confirmation, progress, and result for duplicate image filtering.
 * States: confirm -> processing -> complete | error
 * Threshold: 0 = exact (byte-identical), 1-10 = perceptual hash (higher = more aggressive)
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  Slider,
} from '@mui/material';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import { FileStorageService } from '../../services/FileStorageService';

interface DedupFilterDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  folderPath: string;
  folderName: string;
  onComplete: () => void;
}

type DialogState = 'confirm' | 'processing' | 'complete' | 'error';

interface DedupProgress {
  progress: number;
  currentFolder: string;
  processedFiles: number;
  totalFiles: number;
}

interface DedupResult {
  removedCount: number;
  savedBytes: number;
  processedFolders: number;
  totalFiles: number;
}

const thresholdMarks = [
  { value: 0, label: '0' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
  { value: 6, label: '6' },
  { value: 7, label: '7' },
  { value: 8, label: '8' },
  { value: 9, label: '9' },
  { value: 10, label: '10' },
];

const getThresholdDescription = (value: number): string => {
  if (value === 0) return '완전 동일한 파일만 제거 (가장 안전)';
  if (value <= 2) return '거의 동일한 프레임만 제거';
  if (value <= 5) return '유사한 이미지 제거 (조명 변화 일부 포함)';
  if (value <= 8) return '비슷한 이미지 적극 제거';
  return '매우 공격적 — 대부분의 유사 이미지 제거';
};

export const DedupFilterDialog: React.FC<DedupFilterDialogProps> = ({
  open,
  onClose,
  projectId,
  folderPath,
  folderName,
  onComplete,
}) => {
  const [state, setState] = useState<DialogState>('confirm');
  const [threshold, setThreshold] = useState<number>(0);
  const [progress, setProgress] = useState<DedupProgress>({ progress: 0, currentFolder: '', processedFiles: 0, totalFiles: 0 });
  const [result, setResult] = useState<DedupResult | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const abortRef = useRef<(() => void) | null>(null);

  const handleStart = useCallback(() => {
    setState('processing');

    const abort = FileStorageService.dedupFilter(
      projectId,
      folderPath,
      threshold,
      (p) => setProgress(p),
      (r) => {
        setResult(r);
        setState('complete');
      },
      (msg) => {
        setErrorMsg(msg);
        setState('error');
      },
    );
    abortRef.current = abort;
  }, [projectId, folderPath, threshold]);

  const handleClose = () => {
    if (state === 'processing' && abortRef.current) {
      abortRef.current();
    }
    if (state === 'complete') {
      onComplete();
    }
    // Reset state for next open
    setState('confirm');
    setThreshold(0);
    setProgress({ progress: 0, currentFolder: '', processedFiles: 0, totalFiles: 0 });
    setResult(null);
    setErrorMsg('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={state === 'processing' ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FilterListIcon color="primary" />
        {state === 'confirm' && '중복 이미지 필터'}
        {state === 'processing' && '처리 중...'}
        {state === 'complete' && '필터 완료'}
        {state === 'error' && '오류 발생'}
      </DialogTitle>

      <DialogContent>
        {state === 'confirm' && (
          <Box>
            <Typography gutterBottom>
              &apos;{folderName}&apos; {folderPath === '' ? '전체 학습 폴더' : '폴더'}의 중복 이미지를 제거하시겠습니까?
            </Typography>

            <Box sx={{ mt: 3, px: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                유사도 기준 (Threshold): {threshold}
              </Typography>
              <Slider
                value={threshold}
                onChange={(_, value) => setThreshold(value as number)}
                min={0}
                max={10}
                step={1}
                marks={thresholdMarks}
                valueLabelDisplay="auto"
                sx={{ mt: 1 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {getThresholdDescription(threshold)}
              </Typography>
            </Box>
          </Box>
        )}

        {state === 'processing' && (
          <Box>
            <LinearProgress variant="determinate" value={progress.progress} sx={{ mb: 2, height: 8, borderRadius: 4 }} />
            <Typography variant="body2" color="text.secondary">
              처리 중: {progress.currentFolder} ({progress.processedFiles}/{progress.totalFiles} 파일)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {progress.progress}% 완료
            </Typography>
          </Box>
        )}

        {state === 'complete' && result && (
          <Box>
            {result.removedCount > 0 ? (
              <>
                <Typography variant="h6" color="success.main" gutterBottom>
                  {result.removedCount}개 중복 파일 삭제
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {FileStorageService.formatFileSize(result.savedBytes)} 절약 | {result.processedFolders}개 폴더 처리 | {result.totalFiles}개 파일 검사
                </Typography>
              </>
            ) : (
              <Typography color="text.secondary">
                중복 이미지가 없습니다. ({result.totalFiles}개 파일 검사)
              </Typography>
            )}
          </Box>
        )}

        {state === 'error' && (
          <Typography color="error">
            {errorMsg}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        {state === 'confirm' && (
          <>
            <Button onClick={handleClose}>취소</Button>
            <Button onClick={handleStart} variant="contained" color="primary">실행</Button>
          </>
        )}
        {state === 'processing' && (
          <Button onClick={handleClose} color="error">중단</Button>
        )}
        {(state === 'complete' || state === 'error') && (
          <Button onClick={handleClose} variant="contained">확인</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
